import React, { useState } from "react";

import { assertCurrentUser } from "lib/currentUser";
import { Opportunity } from "lib/generated/app-service-gql/graphql";
import { NoticesStagesv2 } from "lib/types/graphQLEnums";
import { Qualification } from "lib/types/qualifications";
import { OpportunityEntityType } from "../../lib/types/graphQLEnums";
import { Flex } from "../../styles/utility-components";
import { SaveToButton } from "../opportunities/saving/SaveToButton";
import { HideNoticeButton } from "../record_qualifications/HideNoticeButton";
import BidNoBidPromptModal from "./BidNoBidPromptModal";
import { RestoreNoticeButton } from "./RestoreNoticeButton";

type Props = {
  size?: "small" | "default";
  contextSource: string;
  notice: {
    id: string;
    name: string;
    value: number | undefined;
    procurementStage: {
      id: string;
      stage: string;
      qualification?: Qualification;
    };
    stage: string;
    stageV2?: NoticesStagesv2 | undefined;
    closeDate: string | undefined;
    expiryDate: string | undefined;
    buyers: string[];
    suppliers: string[];
    score: number | null | undefined;
    signalTypes: Record<string, string[]> | undefined;
    opportunities?: Pick<Opportunity, "id">[];
    listCount?: number;
  };
};
export function NoticeActions({ notice, size = "default", contextSource }: Props) {
  const currentUser = assertCurrentUser();

  const [newOpportunityId, setNewOpportunityId] = useState<string | undefined>();
  const [isBidNoBidPromptModalOpen, setIsBidNoBidPromptModalOpen] = useState(false);

  const isOpenTender = notice.stageV2 === NoticesStagesv2.OpenTender;

  const isHidden =
    notice.procurementStage.qualification === "not_relevant" ||
    notice.procurementStage.qualification === "pre_engage_not_relevant";

  if (isHidden) {
    return <RestoreNoticeButton notice={notice} contextSource={contextSource} size={size} />;
  }

  return (
    <Flex gap={8}>
      <SaveToButton
        entityIds={[notice.procurementStage.id]}
        entityType="notice"
        entityData={{ isOpenTender }}
        compactDisplay={size === "small"}
        defaultOpportunityValues={{
          name: notice.name,
          value: notice.value,
          assignedToId: currentUser.guid,
          entities: [
            {
              entityId: notice.procurementStage.id,
              entityType: OpportunityEntityType.Notice,
            },
            ...notice.buyers.map((buyer) => ({
              entityId: buyer,
              entityType: OpportunityEntityType.Organisation,
            })),
            ...notice.suppliers.map((id) => ({
              entityId: id,
              entityType: OpportunityEntityType.Organisation,
            })),
          ],
          closeDate: notice.closeDate,
          // If the notice is in the open tender stage,
          // use the close date as the submission date
          submissionDate:
            notice.stageV2 === NoticesStagesv2.OpenTender ? notice.closeDate : undefined,
          existingExpiryDate: notice.expiryDate,
          noticeStage: notice.procurementStage.stage,
          stage: notice.stage,
        }}
        opportunities={notice.opportunities}
        savedCount={notice.listCount}
        onOpportunitySave={(optId: string) => {
          if (isOpenTender) {
            setNewOpportunityId(optId);
            setIsBidNoBidPromptModalOpen(true);
          }
        }}
      />
      <HideNoticeButton
        noticeName={notice.name}
        procurementStage={notice.procurementStage}
        score={notice.score}
        signalTypes={notice.signalTypes}
        contextSource={contextSource}
        noticeId={notice.id}
        size={size}
      />
      <BidNoBidPromptModal
        isOpen={isBidNoBidPromptModalOpen}
        onOpenChange={(open) => setIsBidNoBidPromptModalOpen(open)}
        newOpportunityId={newOpportunityId}
      />
    </Flex>
  );
}
